import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import _ from 'lodash';
import classes from './tabs.module.scss';
import Loader from '../../../../_common/Loader/Loader';
import Pagination from '../../../../_common/Pagination/Pagination';
import {
  addScriptNoteCopyRequest, updateScriptStatusesRequest, changeScriptData, getPatientScriptsRequest,
  scriptNoteOrAttachmentIds, updateScriptRequest, deleteScriptData, addScriptAttachmentCopyRequest,
  bulkUpdateScriptRequest,
} from '../../../../../store/actions/script';
import accountHelpers from '../../../../../utils/accountHelpers';
import Checkbox from '../../../../_common/Form/Checkbox/Checkbox';
import Button from '../../../../_common/Form/Buttons/Button';
import DOBPicker from '../../../../_common/Form/DatePickers/DOBPicker/DOBPicker';
import Select from '../../../../_common/Form/Selects/Select/Select';
import InputField from '../../../../_common/Form/TextFields/InputField';
import Modal from '../../../../_common/Modal/Modal';
import StatusField from '../../../../_common/StatusField/StatusField';
import api from '../../../../../api/api';
import Utils from '../../../../../utils/utils';

const header = [
  'Bill on', 'Physician', 'Medication', 'Ship on',
  'Status', 'Process on', 'Refill',
  'Remaining', 'Patient pay',
  'Quantity', 'RX number', 'Pouch'];

const statusSelect = ['Received', 'Review', 'Prior Auth',
  'Process', 'Copay Assistance', 'Schedule', 'QA', 'Fill',
  'Shipped', 'Done', 'Cancelled', 'Refill', 'Renew',
];

const limitCounts = [
  {
    id: 0,
    count: 50,
  },
  {
    id: 1,
    count: 75,
  },
  {
    id: 2,
    count: 100,
  },
  {
    id: 3,
    count: 125,
  },
];

const selectableColumns = ['Bill on', 'Status', 'Process on'];

const RXHistory = ({
  scriptStatus, scheduleIds, changeScriptInfo, changeCounts,
}) => {
  const isPatient = accountHelpers.isInOneOfRoles(['Patient', 'Health', 'Manufacturer Users']);
  const isAdmin = accountHelpers.isInOneOfRoles(['Admin', 'Pharmacy']);

  const detailsTabRef = document.getElementById('details-id');

  const firstLoad = useRef(true);

  const dispatch = useDispatch();
  const navigator = useNavigate();
  const {
    patientScriptData, patientScriptTotalPage, attachmentsId, requestStatus, notesId,
  } = useSelector((store) => store.script);

  const { PatientId } = patientScriptData?.[0] || { PatientId: null };
  const { scriptId } = useParams();

  const [scriptIds, setScriptIds] = useState([]);
  const [requestData, setRequestData] = useState({
    userId: accountHelpers.getUser().id,
    patientId: PatientId,
    limit: 50,
    page: 1,
  });
  const [changeData, setChangeData] = useState({
    id: '',
    name: '',
    value: '',
  });

  const [isOpenModal, setIsOpenModal] = useState({ idScript: null, open: false });
  const { idScript, open } = isOpenModal;

  const [loading, setLoading] = useState(false);
  const [copyLoad, setCopyLoad] = useState(false);

  const [columnSelection, setColumnSelection] = useState(null);
  const [isSelectingColumn, setIsSelectingColumn] = useState(false);
  const [isSelectingStarted, setIsSelectingStarted] = useState(false);
  const [isSelectedColumn, setIsSelectedColumns] = useState(false);
  const [isResizing, setIsResizing] = useState(false);

  const [bulkUpdateLoading, setBulkUpdateLoading] = useState(false);

  const [errors, setErrors] = useState({});

  const { limit, page, userId } = requestData;
  const allScriptIds = patientScriptData.map((p) => p.id).filter((p) => p !== +scriptId);
  const isCheckedAllScriptIds = allScriptIds.every((id) => scriptIds.includes(id));

  const allScheduleIds = patientScriptData.filter((p) => +p.id !== +scriptId && p.status === 'Schedule')
    .map((p) => p.id);
  const isCheckedAllScheduleIds = scheduleIds && allScheduleIds.every((id) => scheduleIds.includes(id));

  useEffect(() => {
    (async () => {
      await dispatch(getPatientScriptsRequest(requestData));

      firstLoad.current = false;
    })();
  }, [scriptStatus, requestData]);

  useEffect(() => {
    const closeElement = (e) => {
      if (!e.target.closest('[id="tbody"]') && !e.target.closest('[id^="dob-portal"]')
        && !e.target.closest('[id^="dob-container"]') && !e.target.closest('.react-datepicker__year-dropdown')) {
        if (changeData.id && changeData.name) {
          sendUpdateData(changeData.id, changeData.name, changeData.value);

          setTimeout(() => {
            setChangeData({
              id: '',
              name: '',
              value: '',
            });
          });
        }
      }
    };

    window.addEventListener('click', closeElement);

    return () => {
      window.removeEventListener('click', closeElement);
    };
  }, [changeData, patientScriptData, columnSelection]);

  useEffect(() => () => {
    dispatch(scriptNoteOrAttachmentIds({}));
    changeScriptInfo({ scheduleIds: null });
  }, []);

  useEffect(() => {
    if (_.isEmpty(scriptNoteOrAttachmentIds)) {
      setScriptIds([]);
    }
  }, [attachmentsId, notesId]);

  const sendUpdateData = async (id, name, value) => {
    const currentScriptNote = patientScriptData?.find((f) => f.id === id);

    if (name && value !== currentScriptNote[name]) {
      const { status, payload } = await dispatch(updateScriptRequest({
        [name]: currentScriptNote[name],
        id: currentScriptNote.id,
        userId: accountHelpers.getUser().id,
        patientId: currentScriptNote.PatientId,
        physicianId: currentScriptNote.PhysicianId,
        processedOn: currentScriptNote.processedOn,
        productId: currentScriptNote.ProductId,
      }));

      if (status === 'ok') {
        if (name === 'status' || name === 'processedOn') {
          const reqData = Utils.deleteEmptyKeys({
            fromProcessedOn: name === 'processedOn' ? value : null,
            fromStatus: name === 'status' ? value : null,
            manuallyChanged: true,
            scriptId: id,
            toProcessedOn: name === 'processedOn' ? currentScriptNote[name] : null,
            toStatus: name === 'status' ? currentScriptNote[name] : null,
            userId: accountHelpers.getUser().id,
          });

          await dispatch(updateScriptStatusesRequest(reqData));

          if (+id === +scriptId) {
            changeCounts('Statuses');
          }
        }

        toast.success(`Saved ${currentScriptNote[name]} for ${name} `);
      } else {
        dispatch(changeScriptData({ ...currentScriptNote, ...payload.data.results, type: 'updateAllScript' }));
        toast.error(payload.data.message);

        setErrors((prev) => {
          const newErrors = { ...prev };

          _.set(newErrors, `${payload.data.results.id}.${name}`, true);

          return newErrors;
        });
      }
    }
  };

  const onCheckBoxChange = (id) => {
    if (scheduleIds) {
      if (id === 'all') {
        if (isCheckedAllScheduleIds) {
          changeScriptInfo({ scheduleIds: [] });
        } else {
          changeScriptInfo({ scheduleIds: allScheduleIds });
        }
      } else {
        changeScriptInfo({
          scheduleIds: scheduleIds.includes(id)
            ? scheduleIds.filter((p) => p !== id)
            : [...scheduleIds, id],
        });
      }
    } else if (id === 'all') {
      if (isCheckedAllScriptIds) {
        setScriptIds([]);
      } else {
        setScriptIds(allScriptIds);
      }
    } else if (scriptIds.some((s) => s === id)) {
      setScriptIds(scriptIds.filter((s) => s !== id));
    } else if (id !== +scriptId) {
      setScriptIds([...scriptIds, id]);
    }
  };

  const onCopyClick = async (type) => {
    setCopyLoad(true);

    if (type === 'copy') {
      try {
        if (attachmentsId.length) {
          const { status } = await dispatch(addScriptAttachmentCopyRequest({
            UserId: userId,
            attachmentsId,
            scriptsId: scriptIds,
          }));

          if (status === 'ok') {
            toast.success('Attachment successfully copied');
            setScriptIds([]);
            dispatch(scriptNoteOrAttachmentIds({ name: 'attachment', ids: [] }));
          }
        } else {
          const { status } = await dispatch(addScriptNoteCopyRequest({
            UserId: userId,
            notesId,
            scriptsId: scriptIds,
          }));

          if (status === 'ok') {
            toast.success('Note successfully copied');

            setScriptIds([]);
            dispatch(scriptNoteOrAttachmentIds({ name: 'note', ids: [] }));
          }
        }
      } catch (e) {
        toast.error('Something went wrong');
        setCopyLoad(false);
      }
    } else {
      setScriptIds([]);
      dispatch(scriptNoteOrAttachmentIds({}));
    }

    setCopyLoad(false);
  };

  const onChangePageData = async (path, count) => {
    if (path === 'limit') {
      setRequestData((prev) => ({ ...prev, [path]: count, page: 1 }));
    } else {
      setRequestData((prev) => ({ ...prev, [path]: count }));
    }
  };

  const onChangeDataClick = (id, name, changeValue) => {
    setChangeData({ id, name, value: changeValue });

    if (changeData.id && (changeData.id !== id || changeData.name !== name)) {
      sendUpdateData(changeData.id, changeData.name, changeData.value);
    }
  };

  const onChangeUpdateData = (data, type) => {
    if (type === 'date' && data.length !== 10) {
      return;
    }

    setErrors((prev) => {
      const newErrors = { ...prev };

      _.unset(newErrors, `${changeData.id}.${changeData.name}`);

      return newErrors;
    });

    dispatch(changeScriptData({ ...changeData, value: data }));
  };

  const scriptClick = (ScriptId) => {
    if (isPatient) {
      navigator(`/scripts/${ScriptId}`);
      detailsTabRef.click();
    }
  };

  const deleteScriptClick = async (id) => {
    setLoading(true);

    dispatch(deleteScriptData(id));

    await api.deleteScript(id);

    changeCounts('RX History', -1);

    setLoading(false);

    setIsOpenModal({ idScript: null, open: false });
  };

  const tableHeader = useMemo(() => (+accountHelpers.getUser().id === 601
    ? header.filter((h) => (h !== 'Physician'))
    : header), []);

  const getColumnIndex = (name) => tableHeader.indexOf(name);

  const handleColumnMouseDown = (e, rowIndex, name, columnPath) => {
    if (!selectableColumns.includes(name) || document.querySelector('.react-datepicker__tab-loop')) {
      return;
    }

    setIsSelectingColumn(true);

    setColumnSelection({
      column: getColumnIndex(name),
      name,
      columnPath,
      startRow: rowIndex,
      endRow: rowIndex,
    });

    setIsSelectingStarted(true);
  };

  const handleColumnMouseEnter = (rowIndex, name) => {
    if (!isSelectingColumn || !columnSelection) return;

    const colIndex = getColumnIndex(name);
    if (colIndex !== columnSelection.column) return;

    setIsSelectingStarted(false);
    setIsSelectedColumns(true);

    setColumnSelection((prev) => ({
      ...prev,
      endRow: rowIndex,
    }));

    setChangeData({
      id: '',
      name: '',
      value: '',
    });
  };

  const handleColumnMouseUp = (d, path) => {
    setIsSelectedColumns(false);

    if (!isSelectedColumn && (changeData.id !== d.id || changeData.name !== path)) {
      onChangeDataClick(d.id, path, d[path]);
    }
  };

  const isCellSelected = (rowIndex, name) => {
    if (!columnSelection) return false;

    const colIndex = getColumnIndex(name);
    if (colIndex !== columnSelection.column) return false;

    const min = Math.min(columnSelection.startRow, columnSelection.endRow);
    const max = Math.max(columnSelection.startRow, columnSelection.endRow);

    return rowIndex >= min && rowIndex <= max;
  };

  const isLastSelectedCell = (rowIndex, name) => {
    if (!columnSelection) return false;

    const colIndex = getColumnIndex(name);
    if (colIndex !== columnSelection.column) return false;

    const max = Math.max(columnSelection.startRow, columnSelection.endRow);

    return rowIndex === max;
  };

  const handleResizeStart = (e) => {
    e.stopPropagation();
    e.preventDefault();

    setIsResizing(true);
    setIsSelectingColumn(false);
    setIsSelectedColumns(true);
  };

  useEffect(() => {
    if (!isResizing || !columnSelection) return;

    const move = (e) => {
      const rows = document.querySelectorAll('#tbody tr');

      rows.forEach((row, index) => {
        const rect = row.getBoundingClientRect();

        if (e.clientY >= rect.top && e.clientY <= rect.bottom) {
          setColumnSelection((prev) => ({
            ...prev,
            endRow: index,
          }));
        }
      });
    };

    document.addEventListener('mousemove', move);
    return () => document.removeEventListener('mousemove', move);
  }, [isResizing, columnSelection]);

  const handleAutoScroll = (e) => {
    const margin = 100;
    const speed = 20;

    if (e.clientY < margin) window.scrollBy(0, -speed);
    else if (e.clientY > window.innerHeight - margin) window.scrollBy(0, speed);
  };

  useEffect(() => {
    if (!isSelectingColumn && !isResizing) return;

    const move = (e) => handleAutoScroll(e);

    document.addEventListener('mousemove', move);
    return () => document.removeEventListener('mousemove', move);
  }, [isSelectingColumn, isResizing]);

  useEffect(() => {
    if (columnSelection) {
      const clearColumn = (e) => {
        if (!e.target.closest('[id="tbody"]') && !e.target.closest('[id^="dob-portal"]')
          && !e.target.closest('[id^="dob-container"]')) {
          setIsSelectingColumn(false);
          setIsResizing(false);
          setIsSelectedColumns(false);
          setColumnSelection(null);
        }
      };

      document.addEventListener('mousedown', clearColumn);

      return () => document.removeEventListener('mousedown', clearColumn);
    }
  }, [columnSelection]);

  useEffect(() => {
    const stop = async () => {
      setIsSelectingColumn(false);
      setIsResizing(false);
      setIsSelectedColumns(false);

      if (columnSelection && columnSelection.startRow !== columnSelection.endRow) {
        const ids = [];
        const startRowValue = patientScriptData.find((p, i) => columnSelection.startRow === i)[columnSelection.columnPath];

        patientScriptData.forEach((col, index) => {
          const startIndex = columnSelection.startRow > columnSelection.endRow ? columnSelection.endRow : columnSelection.startRow;
          const endIndex = columnSelection.startRow > columnSelection.endRow ? columnSelection.startRow : columnSelection.endRow;

          if ((startRowValue !== col[columnSelection.columnPath]) && index >= startIndex && index <= endIndex) {
            ids.push(col.id);
          }
        });

        if (ids.length) {
          setBulkUpdateLoading(true);

          const { payload } = await dispatch(bulkUpdateScriptRequest({
            id: ids,
            userId: accountHelpers.getUser().id,
            [columnSelection.columnPath]: startRowValue,
          }));

          toast.success(`"${columnSelection.name}" column is updated`);

          if (payload.data.errors?.length) {
            toast.error('Some script can not be updated as you are not authorized to update this scrip');

            setErrors((prev) => {
              const newErrors = { ...prev };

              payload.data.errors.forEach((e) => {
                _.set(newErrors, `${e}.${columnSelection.columnPath}`, true);
              });

              return newErrors;
            });
          }

          if (columnSelection.columnPath === 'status' || columnSelection.columnPath === 'processedOn') {
            for (const p of payload.data.prev) {
              const reqData = Utils.deleteEmptyKeys({
                fromProcessedOn: columnSelection.columnPath === 'processedOn' ? p[columnSelection.columnPath] : null,
                fromStatus: columnSelection.columnPath === 'status' ? p[columnSelection.columnPath] : null,
                manuallyChanged: true,
                scriptId: p.id,
                toProcessedOn: columnSelection.columnPath === 'processedOn' ? startRowValue : null,
                toStatus: columnSelection.columnPath === 'status' ? startRowValue : null,
                userId: accountHelpers.getUser().id,
              });

              if ((columnSelection.columnPath === 'status' && reqData.fromStatus !== reqData.toStatus)
                || (columnSelection.columnPath === 'processedOn' && reqData.fromProcessedOn !== reqData.toProcessedOn)) {
                await dispatch(updateScriptStatusesRequest(reqData));

                if (+p.id === +scriptId) {
                  changeCounts('Statuses');
                }
              }
            }
          }

          setBulkUpdateLoading(false);
        } else {
          toast.info('There is nothing to update');
        }
      }
    };

    window.addEventListener('mouseup', stop);
    return () => window.removeEventListener('mouseup', stop);
  }, [columnSelection]);

  return (
    <div
      className={classes.script_rx_wrapper}
      style={{ userSelect: isSelectingColumn ? 'none' : 'inherit' }}
    >
      {(!!(attachmentsId.length || notesId.length)) && (
        <div className={classes.copy_button_wrapper}>
          <div className={classes.note_button_header}>
            <Button
              reverseColor
              title="Copy"
              onClick={() => onCopyClick('copy')}
              buttonColor="#5F6C8E"
              loading={copyLoad}
            />
          </div>

          <div className={classes.note_button_header}>
            <Button
              title="Cancel"
              clearButton
              onClick={() => onCopyClick('cancel')}
            />
          </div>
        </div>
      )}

      {requestStatus === 'request' && firstLoad.current && (
        <Loader className={classes.loader_wrapper} />
      )}

      {requestStatus === 'success'
        && (
          <>
            {!patientScriptData.length
              ? <div className={classes.notResult}>No Data Found</div>
              : (
                <div className={classes.table_wrapper}>
                  <div>
                    {requestStatus === 'request' && (
                      <Loader className={classes.loader} />
                    )}
                    <table>
                      <thead onMouseDown={() => !isPatient && setColumnSelection(null)}>
                        <tr className={isAdmin ? classes.tr : ''}>
                          {isAdmin && <th>Delete</th>}

                          {!isPatient && (
                          <th>
                            <Checkbox
                              disabled={(!scheduleIds || !allScheduleIds.length)
                                && ((!attachmentsId.length || !notesId.length) || !allScriptIds.length)}
                              onChange={() => onCheckBoxChange('all')}
                              checked={isCheckedAllScriptIds || isCheckedAllScheduleIds}
                            />
                          </th>
                          )}

                          {tableHeader.map((h) => (
                            <th key={h}>{h}</th>
                          ))}
                        </tr>
                      </thead>

                      <tbody id="tbody">
                        {patientScriptData.map((d, index) => {
                          const disabledCheckbox = (!scheduleIds || +d.id === +scriptId || d.status !== 'Schedule')
                          && ((!attachmentsId.length && !notesId.length) || !allScriptIds.includes(d.id));
                          const isChecked = scriptIds.includes(d.id) || scheduleIds?.includes(d.id);

                          return (
                            <tr
                              key={`${d.id}`}
                              className={`${isAdmin ? classes.tr_admin : classes.tr} ${isChecked ? classes.active : ''}`}
                              onClick={() => scriptClick(d.id)}
                            >
                              {isAdmin && (
                                <td onMouseDown={() => !isPatient && setColumnSelection(null)}>
                                  <button
                                    className={classes.delete_button}
                                    type="button"
                                    onClick={() => setIsOpenModal({ idScript: d.id, open: true })}
                                    disabled={+d.id === +scriptId}
                                  >
                                    Delete
                                  </button>
                                </td>
                              )}

                              {!isPatient && (
                                <td onMouseDown={() => !isPatient && setColumnSelection(null)}>
                                  <Checkbox
                                    disabled={disabledCheckbox}
                                    onChange={() => onCheckBoxChange(d.id)}
                                    checked={isChecked}
                                  />
                                </td>
                              )}

                              <td
                                className={!isSelectingStarted && isCellSelected(index, 'Bill on') ? classes.cell_selected : ''}
                                onMouseDown={(e) => !isPatient && handleColumnMouseDown(e, index, 'Bill on', 'billOnDate')}
                                onMouseEnter={() => !isPatient && handleColumnMouseEnter(index, 'Bill on')}
                                onMouseUp={() => !isPatient && handleColumnMouseUp(d, 'billOnDate')}
                              >
                                {(changeData.id === d.id && changeData.name === 'billOnDate' && !isPatient)
                                  ? (
                                    <DOBPicker
                                      hideClearButton
                                      date={d.billOnDate}
                                      wrapperClassName={classes.calendar_wrapper}
                                      dateFormat="YYYY-MM-DD"
                                      onChange={(date) => onChangeUpdateData(date, 'date')}
                                    />
                                  )
                                  : d.billOnDate
                                    ? moment(d.billOnDate).format('MM/DD/YYYY')
                                    : '-'}

                                {!isSelectingStarted && isLastSelectedCell(index, 'Bill on') && (
                                <>
                                  {bulkUpdateLoading && (
                                    <Loader
                                      className={classes.column_loader}
                                      size={13}
                                      borderWidth={2}
                                    />
                                  )}

                                  <div
                                    className={classes.fill_handle}
                                    onMouseDown={handleResizeStart}
                                  >
                                    +
                                  </div>
                                </>
                                )}
                              </td>

                              {tableHeader.includes('Physician') && (
                              <td onMouseDown={() => !isPatient && setColumnSelection(null)}>
                                {d.Physician?.firstName ? `${d.Physician?.firstName} ${d.Physician?.lastName}` : ' - '}
                              </td>
                              )}

                              <td onMouseDown={() => !isPatient && setColumnSelection(null)}>
                                <NavLink
                                  onClick={() => detailsTabRef.click()}
                                  to={`/scripts/${d.id}`}
                                >
                                  {d.Product.name}
                                </NavLink>
                              </td>

                              <td
                                className={accountHelpers.getUser().id === 601 ? classes.td : ''}
                                onMouseDown={() => !isPatient && setColumnSelection(null)}
                              >
                                {d.shipOn ? moment(d.shipOn).format('MM/DD/YYYY') : '-'}
                              </td>

                              <td
                                className={!isSelectingStarted && isCellSelected(index, 'Status') ? classes.cell_selected : ''}
                                onMouseDown={(e) => !isPatient && handleColumnMouseDown(e, index, 'Status', 'status')}
                                onMouseEnter={() => !isPatient && handleColumnMouseEnter(index, 'Status')}
                                onMouseUp={() => !isPatient && handleColumnMouseUp(d, 'status')}
                              >
                                {(changeData.id === d.id && changeData.name === 'status' && !isPatient)
                                  ? (
                                    <Select
                                      id="_rx"
                                      options={statusSelect}
                                      currentOption={d.status}
                                      onChange={(option) => onChangeUpdateData(option)}
                                      optionWrapperHeight={390}
                                    />
                                  )

                                  : (
                                    <div className={errors[d.id]?.status ? classes.error : classes.status_cell}>
                                      {d.status
                                        ? <StatusField status={d.status} />

                                        : '-'}
                                    </div>
                                  )}

                                {!isSelectingStarted && isLastSelectedCell(index, 'Status') && (
                                <>
                                  {bulkUpdateLoading && (
                                    <Loader
                                      className={classes.column_loader}
                                      size={13}
                                      borderWidth={2}
                                    />
                                  )}

                                  <div
                                    className={classes.fill_handle}
                                    onMouseDown={handleResizeStart}
                                  >
                                    +
                                  </div>
                                </>
                                )}
                              </td>

                              <td
                                className={!isSelectingStarted && isCellSelected(index, 'Process on') ? classes.cell_selected : ''}
                                onMouseDown={(e) => !isPatient && handleColumnMouseDown(e, index, 'Process on', 'processedOn')}
                                onMouseEnter={() => !isPatient && handleColumnMouseEnter(index, 'Process on')}
                                onMouseUp={() => !isPatient && handleColumnMouseUp(d, 'processedOn')}
                              >
                                {(changeData.id === d.id && changeData.name === 'processedOn' && !isPatient)
                                  ? (

                                    <DOBPicker
                                      hideClearButton
                                      date={d.processedOn}
                                      wrapperClassName={classes.calendar_wrapper}
                                      dateFormat="YYYY-MM-DD"
                                      onChange={(date) => onChangeUpdateData(date, 'date')}
                                    />
                                  )
                                  : d.processedOn
                                    ? moment(d.processedOn).format('MM/DD/YYYY')
                                    : '-'}

                                {!isSelectingStarted && isLastSelectedCell(index, 'Process on') && (
                                <>
                                  {bulkUpdateLoading && (
                                    <Loader
                                      className={classes.column_loader}
                                      size={13}
                                      borderWidth={2}
                                    />
                                  )}

                                  <div
                                    className={classes.fill_handle}
                                    onMouseDown={handleResizeStart}
                                  >
                                    +
                                  </div>
                                </>
                                )}
                              </td>

                              <td
                                onClick={() => onChangeDataClick(d.id, 'refills', d.refills)}
                                onMouseDown={() => !isPatient && setColumnSelection(null)}
                              >
                                {(changeData.id === d.id && changeData.name === 'refills' && !isPatient)
                                  ? (
                                    <InputField
                                      autoFocus
                                      onChange={(v) => onChangeUpdateData(v)}
                                      value={d.refills}
                                      onKeyPress={Utils.keyPressOnlyNumber}
                                      onPaste={(e) => Utils.keyPressOnlyNumber(e, true)}
                                    />
                                  )
                                  : d.refills}
                              </td>

                              <td
                                onClick={() => onChangeDataClick(d.id, 'refillsRemaining', d.refillsRemaining)}
                                onMouseDown={() => !isPatient && setColumnSelection(null)}
                              >
                                {(changeData.id === d.id && changeData.name === 'refillsRemaining' && !isPatient)
                                  ? (
                                    <InputField
                                      autoFocus
                                      onChange={(v) => onChangeUpdateData(v)}
                                      value={d.refillsRemaining}
                                      onKeyPress={Utils.keyPressOnlyNumber}
                                      onPaste={(e) => Utils.keyPressOnlyNumber(e, true)}
                                    />
                                  )
                                  : d.refillsRemaining}
                              </td>

                              <td
                                onClick={() => onChangeDataClick(d.id, 'patientPay', d.patientPay)}
                                onMouseDown={() => !isPatient && setColumnSelection(null)}
                              >
                                {(changeData.id === d.id && changeData.name === 'patientPay' && !isPatient)
                                  ? (
                                    <InputField
                                      autoFocus
                                      onChange={(v) => onChangeUpdateData(v)}
                                      value={d.patientPay}
                                      onKeyPress={Utils.keyPressCurrency}
                                      onPaste={(e) => Utils.keyPressCurrency(e, true)}
                                    />
                                  )
                                  : Utils.formattingCurrency(d.patientPay)}
                              </td>

                              <td
                                onClick={() => onChangeDataClick(d.id, 'quantity', d.quantity)}
                                onMouseDown={() => !isPatient && setColumnSelection(null)}
                              >
                                {(changeData.id === d.id && changeData.name === 'quantity' && !isPatient)
                                  ? (
                                    <InputField
                                      autoFocus
                                      onChange={(v) => onChangeUpdateData(v)}
                                      value={d.quantity}
                                      onKeyPress={Utils.keyPressOnlyNumber}
                                      onPaste={(e) => Utils.keyPressOnlyNumber(e, true)}
                                    />
                                  )
                                  : d.quantity}
                              </td>

                              <td
                                onClick={() => onChangeDataClick(d.id, 'rxNumber', d.rxNumber)}
                                onMouseDown={() => !isPatient && setColumnSelection(null)}
                              >
                                {(changeData.id === d.id && changeData.name === 'rxNumber' && !isPatient)
                                  ? (
                                    <InputField
                                      autoFocus
                                      onChange={(v) => onChangeUpdateData(v)}
                                      value={d.rxNumber}
                                      onKeyPress={Utils.keyPressOnlyNumber}
                                      onPaste={(e) => Utils.keyPressOnlyNumber(e, true)}
                                    />
                                  )
                                  : d.rxNumber}
                              </td>

                              <td
                                onClick={() => onChangeDataClick(d.id, 'pouch', d.pouch)}
                                onMouseDown={() => !isPatient && setColumnSelection(null)}
                              >
                                {(changeData.id === d.id && changeData.name === 'pouch' && !isPatient)
                                  ? (
                                    <div className={classes.checkbox}>
                                      <Checkbox
                                        onChange={(v) => onChangeUpdateData(v)}
                                        checked={d.pouch}
                                      />
                                    </div>
                                  )
                                  : d.pouch ? 'Yes' : 'No'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <Pagination
                    onChange={onChangePageData}
                    limit={+limit}
                    limitCounts={limitCounts}
                    page={+page - 1}
                    totalPage={+patientScriptTotalPage}
                  />
                </div>
              )}

          </>
        )}

      <Modal open={open} onClose={() => setIsOpenModal({ idScript: null, open: false })}>
        <div className={classes.prescript_modal_wrapper}>
          <h2>Will you like to delete this entry</h2>

          <div>
            <Button
              title="NO"
              onClick={() => setIsOpenModal({ idScript: null, open: false })}
              clearButton
            />

            <Button
              title="YES"
              onClick={() => deleteScriptClick(idScript)}
              loading={loading}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

RXHistory.propTypes = {
  scriptStatus: PropTypes.string,
  scheduleIds: PropTypes.any,
  changeScriptInfo: PropTypes.func.isRequired,
  changeCounts: PropTypes.func.isRequired,
};

RXHistory.defaultProps = {
  scriptStatus: '',
  scheduleIds: null,
};

export default RXHistory;
